import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Alert,
	Card,
	Space,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox } from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useQueryClient } from 'react-query';
import { CargoContractSides } from '@shared/utils/constants';
import { splitActionKey } from '@shared/utils/splitActionKey';
import type { CargoPortProps } from '@api/models/cargo-port';
import { updateCargoPort } from '@client/lib/api';
import CargoContractSideTag from '@client/screens/cargos/details/utils/CargoContractSideTag';
import { useVoyage } from '../../components/VoyageProvider/VoyageProvider';
import FreightTablesTab from './FreightTablesTab';

const FreightTab = () => {
	const {
		cargos,
		refreshVoyageDetails,
		refreshCargos,
		refreshVoyageInvoices,
		refreshFixtureDetails,
		refreshDas,
		hasCargoRelet,
	} = useVoyage();

	const queryClient = useQueryClient();

	const [loading, setLoading] = useState(false);
	const [selectedCargoId, setSelectedCargoId] = useState<number | null>(null);

	useEffect(() => {
		refreshVoyageDetails();
		refreshCargos();
	}, [refreshVoyageDetails, refreshCargos]);

	const sortedCargos = useMemo(() => {
		return [...(cargos ?? [])].sort((a, b) => {
			if (a.contractSide === b.contractSide) return a.id - b.id;

			return a.contractSide === CargoContractSides.VC_OUT ? -1 : 1;
		});
	}, [cargos]);

	useEffect(() => {
		if (sortedCargos.length > 0 && selectedCargoId == null) {
			setSelectedCargoId(sortedCargos[0].id);
		}
	}, [sortedCargos, selectedCargoId]);

	const selectedCargo = useMemo(() => {
		if (selectedCargoId == null) {
			return null;
		}

		return sortedCargos.find((c) => c.id === selectedCargoId) ?? null;
	}, [sortedCargos, selectedCargoId]);

	const tabList = sortedCargos.map((c) => ({
		key: String(c.id),
		label: (
			<Space>
				<FontAwesomeIcon icon={faBox as IconProp} />
				{hasCargoRelet ? (
					<CargoContractSideTag
						contractSide={
							c.contractSide === CargoContractSides.VC_IN ?
								CargoContractSides.VC_IN :
								CargoContractSides.VC_OUT
						}
						chartererName={c.FixtureCounterparty.name}
					/>
				) : (
					<span>{`${c.type} for ${c.FixtureCounterparty.name}`}</span>
				)}
			</Space>
		),
	}));

	const getLinkedCargoPort = useCallback((sourceCargoId: number, sourceCargoPortId: number) => {
		const sourceCargo = cargos?.find((cargo) => cargo.id === sourceCargoId);

		if (sourceCargo?.contractSide !== CargoContractSides.VC_OUT) {
			return null;
		}

		const sourceCargoPort = sourceCargo.CargoPorts.find((port) => port.id === sourceCargoPortId);

		if (sourceCargoPort == null) {
			return null;
		}

		const linkedCargo = cargos?.find((cargo) => (
			cargo.contractSide === CargoContractSides.VC_IN
		));

		if (linkedCargo == null) {
			return null;
		}

		const sourceKey = splitActionKey(sourceCargoPort.portAndActionKey);
		const linkedCargoPort = linkedCargo.CargoPorts.find((port) => {
			const targetKey = splitActionKey(port.portAndActionKey);

			return (
				targetKey.action === sourceKey.action &&
				targetKey.index === sourceKey.index
			);
		});

		if (linkedCargoPort == null) {
			return null;
		}

		return {
			cargo: linkedCargo,
			cargoPort: linkedCargoPort,
		};
	}, [cargos]);

	const onUpdateQuantity = useCallback(async (
		attributes: Partial<Record<keyof CargoPortProps, any>>,
		cp: { id: number },
	) => {
		if (selectedCargo?.id == null) {
			return;
		}

		const cargoPort = selectedCargo.CargoPorts.find((entry) => entry.id === cp.id);

		if (cargoPort == null || attributes == null) {
			return;
		}

		setLoading(true);

		await updateCargoPort({
			cargoPortId: cp.id,
			cargoId: selectedCargo.id,
			attributes,
		});

		const linked = getLinkedCargoPort(selectedCargo.id, cp.id);

		if (linked != null) {
			try {
				await updateCargoPort({
					cargoPortId: linked.cargoPort.id,
					cargoId: linked.cargo.id,
					attributes,
				});
			} catch (error) {
				console.error('Failed to update linked cargo port:', error);
			}
		}

		refreshVoyageDetails();
		refreshCargos();
		refreshVoyageInvoices();
		refreshDas();
		await refreshFixtureDetails();
		setLoading(false);
		queryClient.invalidateQueries(['vendorExpenses']);
	}, [
		selectedCargo?.id,
		selectedCargo?.CargoPorts,
		refreshVoyageDetails,
		refreshCargos,
		refreshVoyageInvoices, queryClient, refreshFixtureDetails,
		getLinkedCargoPort,
		refreshDas,
	]);

	return (
		<Card
			onTabChange={(key: string) => setSelectedCargoId(Number(key))}
			tabList={tabList}
		>
			{selectedCargo == null ? 'No cargo selected' : (
				<>
					{selectedCargo.contractSide === CargoContractSides.VC_IN &&
						<Alert
							type="info"
							showIcon
							message="BL Quantity and BL Date are maintained in the VC-Out cargo and synchronized here."
						/>
					}
					<FreightTablesTab
						selectedCargo={selectedCargo}
						onQuantityChange={onUpdateQuantity}
						loading={loading}
					/>
				</>
			)}
		</Card>
	);
};

export default FreightTab;

